
import React, { useState, useEffect, useRef } from 'react';
import { Auth } from 'aws-amplify';
import {
  Button,
  TextField,
  Alert,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import axios from 'axios';

const SMS_CODE_TTL_SECONDS = 60; // 1 minutes

/**
 * MfaStep handles MFA code verification for returning users.
 * Supports both SMS_MFA and SOFTWARE_TOKEN_MFA challenge types.
 * Automatically detects the challenge type from the user session.
 */
function MfaStep({ userSession, onResetSuccess, onBack, onResend }) {
  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(SMS_CODE_TTL_SECONDS);
  const timerRef = useRef(null);

  const challengeType = userSession?.challengeName || 'SMS_MFA';
  const isTOTP = challengeType === 'SOFTWARE_TOKEN_MFA';

  const startCountdown = () => {
    clearInterval(timerRef.current);
    setCountdown(SMS_CODE_TTL_SECONDS);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Start countdown only for SMS MFA
  useEffect(() => {
    if (isTOTP) return;
    startCountdown();
    return () => clearInterval(timerRef.current);
  }, [isTOTP]);

  const formatCountdown = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleResendCode = async () => {
    if (!onResend) return;
    setResending(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      await onResend();
      setCode('');
      startCountdown();
      setSuccessMessage('A new code has been sent to your phone.');
    } catch (err) {
      console.error('Resend MFA code error:', err);
      setErrorMessage('Failed to resend code. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleConfirmMfa = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setLoading(true);

    try {
      await Auth.confirmSignIn(userSession, code, challengeType);

      // After MFA, verify the user's setup is complete
      const currentUser = await Auth.currentAuthenticatedUser({ bypassCache: true });
      const attributes = currentUser.attributes || {};
      const phoneVerified =
        attributes.phone_number_verified === 'true' || attributes.phone_number_verified === true;
      const emailVerified =
        attributes.email_verified === 'true' || attributes.email_verified === true;

      if (!phoneVerified || !emailVerified) {
        // User still has incomplete verification — but they passed MFA
        // This shouldn't normally happen, but handle gracefully
        console.warn('User passed MFA but has unverified attributes:', {
          phoneVerified,
          emailVerified,
        });
      }

      window.location.href = '/'; // Redirect to dashboard
    } catch (error) {
      console.error('MFA error:', error);
      if (error.code === 'CodeMismatchException') {
        setErrorMessage('Invalid code. Please check and try again.');
      } else if (error.code === 'ExpiredCodeException') {
        setErrorMessage('Code has expired. Please request a new one.');
      } else {
        setErrorMessage('Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetToSms = async () => {
    setResetting(true);
    setErrorMessage('');

    try {
      const username = userSession?.username;
      await axios.post(`${process.env.REACT_APP_AWS_API_EXTERNAL_URL}/auth/reset_mfa`, {
        username,
      });

      setResetDialogOpen(false);
      if (onResetSuccess) onResetSuccess();
    } catch (error) {
      console.error('Reset MFA error:', error);
      setErrorMessage('Failed to reset authenticator. Please contact your administrator.');
      setResetDialogOpen(false);
    } finally {
      setResetting(false);
    }
  };

  return (
    <form
      onSubmit={handleConfirmMfa}
      style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '300px' }}
    >
      <Typography variant="body2" style={{ textAlign: 'center', marginBottom: '5px' }}>
        {isTOTP
          ? 'Enter the code from your authenticator app.'
          : 'Enter the verification code sent to your phone.'}
      </Typography>

      {errorMessage && (
        <Alert severity="error" style={{ marginBottom: '10px' }}>
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" style={{ marginBottom: '10px' }}>
          {successMessage}
        </Alert>
      )}
      <TextField
        label={isTOTP ? 'Authenticator Code' : 'SMS Code'}
        variant="outlined"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        required
        disabled={loading}
        autoFocus
      />
      <Button
        type="submit"
        color="primary"
        variant="contained"
        size="large"
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} /> : 'Verify'}
      </Button>

      {/* SMS resend button — shown only for SMS, enabled only when code has expired */}
      {!isTOTP && (
        <Button
          variant="outlined"
          size="large"
          disabled={countdown > 0 || resending || loading}
          onClick={handleResendCode}
          style={{ textTransform: 'none' }}
        >
          {resending ? (
            <CircularProgress size={20} />
          ) : countdown > 0 ? (
            `Resend Code (${formatCountdown(countdown)})`
          ) : (
            'Resend Code'
          )}
        </Button>
      )}

      {/* Back to Login */}
      <Button
        variant="text"
        size="small"
        onClick={() => onBack && onBack()}
        disabled={loading}
        style={{ textTransform: 'none', marginTop: '2px' }}
      >
        Back to Login
      </Button>

      {isTOTP && (
        <>
          <Button
            variant="text"
            color="secondary"
            size="small"
            onClick={() => setResetDialogOpen(true)}
            disabled={loading || resetting}
            style={{ textTransform: 'none', marginTop: '5px' }}
          >
            Lost my authenticator
          </Button>

          <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
            <DialogTitle>Reset Authenticator</DialogTitle>
            <DialogContent>
              <DialogContentText>
                This will reset your MFA back to SMS. You will need to log in again and will receive
                a verification code via text message instead of your authenticator app.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setResetDialogOpen(false)}
                disabled={resetting}
                variant="outlined"
                color="tertiary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleResetToSms}
                disabled={resetting}
                variant="contained"
                color="primary"
              >
                {resetting ? <CircularProgress size={20} /> : 'Reset to SMS'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </form>
  );
}

export default MfaStep;
